/**
 * Shared helpers for the in-app document preview.
 * Ported from partner_web_app so both apps preview delivery / entity docs
 * with the same file-type detection and viewer fallbacks.
 */

const IMAGE_HOSTS = new Set([
  'picsum.photos',
  'images.unsplash.com',
  'plus.unsplash.com',
])

const IMAGE_EXTS = new Set([
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg',
  'heic', 'heif', 'avif', 'tiff', 'tif', 'ico',
])

const OFFICE_EXTS = new Set([
  'doc', 'docx', 'odt', 'rtf',
  'xls', 'xlsx', 'ods',
  'ppt', 'pptx', 'odp',
])

const TEXT_EXTS = new Set([
  'txt', 'log', 'md', 'markdown', 'csv', 'tsv',
  'json', 'yml', 'yaml', 'xml',
])

export type PreviewKind = 'pdf' | 'word' | 'image' | 'text' | 'unknown'

export function extensionOf(url: string): string {
  if (!url) return ''
  try {
    const p = new URL(url).pathname.toLowerCase()
    const dot = p.lastIndexOf('.')
    return dot >= 0 ? p.slice(dot + 1) : ''
  } catch {
    const m = /\.([a-z0-9]{1,6})(?:\?|#|$)/i.exec(url)
    return m ? m[1].toLowerCase() : ''
  }
}

export function previewKind(url: string): PreviewKind {
  if (!url) return 'unknown'
  const ext = extensionOf(url)
  if (ext === 'pdf') return 'pdf'
  if (OFFICE_EXTS.has(ext)) return 'word'
  if (IMAGE_EXTS.has(ext)) return 'image'
  if (TEXT_EXTS.has(ext)) return 'text'
  try {
    const host = new URL(url).hostname.toLowerCase()
    if (IMAGE_HOSTS.has(host)) return 'image'
  } catch { /* malformed URL */ }
  return 'unknown'
}

export function officeViewerUrl(url: string): string {
  return `https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`
}

export function fileNameFromUrl(url: string): string {
  if (!url) return 'file'
  try {
    const path = new URL(url).pathname
    return decodeURIComponent(path.split('/').pop() || 'file')
  } catch {
    return url.split('/').pop() || 'file'
  }
}

export function prettyDocType(type: string): string {
  if (!type) return ''
  return type
    .split(/[_-]/)
    .map((w) => {
      const lower = w.toLowerCase()
      if (lower === 'kra') return 'KRA'
      if (lower === 'pin') return 'PIN'
      if (lower === 'id') return 'ID'
      if (lower === 'psv') return 'PSV'
      if (lower === 'ntsa') return 'NTSA'
      return w.charAt(0).toUpperCase() + w.slice(1)
    })
    .join(' ')
}
